import React, { ChangeEvent } from 'react';
import { Button, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import { Language, LanguageValue, MyInfo } from '@/types/api';
import useLanguageSettingContentProps from '@/components/MenuDrawer/MyMenuDrawer/@hooks/useLanguageSettingContentProps';
import { ContentDivider } from '@/components/MenuDrawer/MyMenuDrawer/@components/ContentDescription';
import { MyMenuDrawerProps } from '@/components/MenuDrawer/MyMenuDrawer';
import { useMypageEventTracker } from '@/hooks/gtm/useGTMEventTracker';
import { MypageEventName } from '@/constant/gtm';
import { useTranslation } from 'react-i18next';

export type SubContentProps = Pick<MyMenuDrawerProps, 'myInfo' | 'menuOnClose'> & {
  readonly openSubMenu?: MyMenuDrawerProps['openSubMenu'];
};

export type LanguageSettingContentProps = Pick<MyInfo, 'language'> & {
  readonly selectedLanguage: LanguageValue;
  readonly selectOptions: { value: string; label: string }[];
  readonly handleSelect: (
    event: ChangeEvent<HTMLInputElement>,
    value: string,
  ) => void;
  readonly handleChange: () => void;
  readonly isChangeButtonDisabled: boolean;
  readonly isLoading: boolean;
};

const LanguageSettingContent = ({ myInfo }: SubContentProps) => {
  const { t } = useTranslation();
  const {
    language,
    selectedLanguage,
    selectOptions,
    handleSelect,
    handleChange,
    isChangeButtonDisabled,
    isLoading,
  } = useLanguageSettingContentProps({ myInfo });
  const mypageEventTracker = useMypageEventTracker();

  const onClickLanguage = (
    event: ChangeEvent<HTMLInputElement>,
    value: string,
  ) => {
    handleSelect(event, value);

    // GA Mypage - 18번
    mypageEventTracker(MypageEventName.clickEvent, {
      area_name: '언어 설정',
      contents: Language[value as LanguageValue] || value,
      status: undefined,
      description: '언어 선택',
    });
  };

  const onClickChange = () => {
    handleChange();

    // GA Mypage - 19번
    mypageEventTracker(MypageEventName.clickEvent, {
      area_name: '언어 설정',
      contents: '변경하기',
      status: undefined,
      description: '언어 변경하기',
    });
  };

  return (
    <ContentDivider>
      <RadioGroup
        defaultValue={language}
        name="language"
        value={selectedLanguage}
        onChange={onClickLanguage}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
          margin: '24px 0 32px',
          '& .MuiFormControlLabel-root': {
            position: 'relative',
            display: 'flex',
            margin: 0,
            gap: '12px',
          },
          '& .MuiButtonBase-root': {
            padding: 0,
          },
          '[data-default="true"]::after': {
            content: `"${t('사용중')}"`,
            display: 'inline-block',
            position: 'absolute',
            right: 0,
            padding: '4px 12px',
            borderRadius: '999px',
            backgroundColor: 'rgba(0, 102, 204, 0.12)',
            fontSize: '12px',
            color: 'var(--dk-accent, #0066CC)',
          },
        }}
      >
        {selectOptions.map(({ value, label }) => (
          <FormControlLabel
            key={`${value}`}
            value={value}
            control={<Radio />}
            label={label}
            data-default={value === language}
          />
        ))}
      </RadioGroup>
      <Button
        variant={'contained'}
        size={'large'}
        disabled={isChangeButtonDisabled}
        loading={isLoading}
        fullWidth
        onClick={onClickChange}
        sx={{ marginTop: '24px' }}
      >
        {t('변경하기')}
      </Button>
    </ContentDivider>
  );
};

export default LanguageSettingContent;
